import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { CONTACT_EMAIL, LOGO_ICON } from "../lib/site";

const Field = ({ label, id, type = "text", value, onChange, textarea }) => (
  <div className="group relative">
    <label
      htmlFor={id}
      className="mb-3 block font-body text-[10px] uppercase tracking-[0.3em] text-ink/50"
    >
      {label}
    </label>
    {textarea ? (
      <textarea
        id={id}
        data-testid={`contact-${id}`}
        rows={3}
        value={value}
        onChange={onChange}
        className="w-full resize-none border-b border-gold/25 bg-transparent pb-3 font-body text-lg text-ink outline-none transition-colors duration-300 focus:border-gold"
      />
    ) : (
      <input
        id={id}
        type={type}
        data-testid={`contact-${id}`}
        value={value}
        onChange={onChange}
        className="w-full border-b border-gold/25 bg-transparent pb-3 font-body text-lg text-ink outline-none transition-colors duration-300 focus:border-gold"
      />
    )}
  </div>
);

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    toast.success("Thank you — reach us directly at " + CONTACT_EMAIL, {
      description: "We'll be in touch shortly.",
    });
    setForm({ name: "", email: "", message: "" });
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative mx-auto max-w-[1500px] px-8 py-32 md:px-16 md:py-48"
    >
      <div className="grid grid-cols-1 gap-16 md:grid-cols-12 md:gap-20">
        {/* Left */}
        <div className="md:col-span-5">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-4 font-body text-xs uppercase tracking-[0.4em] text-gold"
          >
            Contact Us
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="font-display text-4xl font-light leading-tight text-ink md:text-6xl"
          >
            Let's build your
            <span className="italic text-gold"> next award.</span>
          </motion.h2>

          <div className="deco-divider my-10 w-32" />

          <p className="mb-3 font-body text-[10px] uppercase tracking-[0.3em] text-ink/50">
            Write to us directly
          </p>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            data-testid="contact-email-link"
            className="group inline-flex items-center gap-2 font-display text-2xl font-light text-ink transition-colors duration-300 hover:text-gold md:text-3xl"
          >
            {CONTACT_EMAIL}
            <ArrowUpRight
              size={22}
              className="text-gold transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </a>

          <img
            src={LOGO_ICON}
            alt="Metropolis Solutions"
            className="mt-16 hidden h-28 w-28 rounded-full opacity-70 ring-1 ring-gold/30 md:block"
          />
        </div>

        {/* Form */}
        <div className="md:col-span-6 md:col-start-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="flex flex-col gap-10 rounded-t-[80px] border border-gold/20 bg-eggshell-800/60 p-8 backdrop-blur-md md:p-12"
          >
            <Field label="Full Name" id="name" value={form.name} onChange={set("name")} />
            <Field label="Email Address" id="email" type="email" value={form.email} onChange={set("email")} />
            <Field label="How can we help?" id="message" textarea value={form.message} onChange={set("message")} />

            <button
              type="submit"
              data-testid="contact-submit"
              className="group relative mt-2 overflow-hidden self-start rounded-full border border-gold px-10 py-4 font-body text-xs uppercase tracking-[0.28em] text-gold transition-colors duration-500 hover:text-eggshell"
            >
              <span className="relative z-10">Send Inquiry</span>
              <span className="absolute inset-0 -translate-x-full bg-gold transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-0" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};
